import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, type ReactNode } from 'react';

import { ScreenHeader } from '@/components/ScreenHeader';
import { DUR } from '@/lib/motion';
import { cn } from '@/lib/utils';

export interface MastheadProps {
  /** Eyebrow above the title, set in `text-kicker`. */
  overline?: string;
  /** Screen title — the page's one real `<h1>`. */
  title: string;
  /**
   * Retired: mastheads carry no per-header wash (the only canvas tint is a
   * title-led view's `useAmbient`, ui-ux.md §2 "Ambient wash"). Kept optional
   * so existing call sites compile; ignored.
   */
  tint?: string;
  /** A line under the title (e.g. a title count) — forwarded to ScreenHeader. */
  meta?: ReactNode;
  /** Controls beside the title block (e.g. Profile's Edit) — forwarded to ScreenHeader. */
  actions?: ReactNode;
  /** Controlled: feed this from `useCollapsingHeader`. */
  collapsed: boolean;
  /**
   * Extra controls pinned inside the sticky slot below the compact title
   * (e.g. Discover's search field + scope group).
   */
  children?: ReactNode;
  className?: string;
}

/**
 * The shared collapsing screen masthead (ui-ux.md §3): the sentence-case
 * `text-kicker` + Bricolage `text-display`/`text-display-sm` heading pair on
 * the plain canvas (no glow — colour comes from the covers below), and the
 * sticky compact-title slot.
 *
 * The compact title costs no in-flow space: the sticky wrapper is zero-height
 * (or exactly as tall as pinned `children`) and the compact bar is absolutely
 * positioned just above it, so it overlays the content as it cross-fades in
 * (`DUR.fast`) and neither collapsing nor expanding moves anything below. The
 * wrapper sticks at `--masthead-compact-h` (index.css), leaving the bar the
 * strip above it. The compact copy is `aria-hidden` — the `<h1>` stays the one
 * real heading. Fills are deliberately opaque (`bg-ambient-canvas`, the canvas plus the page's ambient wash pinned to the viewport): no backdrop-filter, no
 * translucent glass (§1 principle 5).
 *
 * Spacing assumes the screen's `gap-8` column (the wrapper's `-mt-8` cancels
 * its own gap): the header's bottom padding is the masthead → first-section
 * rhythm (32 mobile / 48 desktop, §2), or with pinned children a tighter
 * in-group gap above them and the rhythm below them.
 *
 * A sticky element pins only within its parent block, so for the compact title
 * to stay pinned for the whole page, render the Masthead inside (or as) the
 * screen's scroll column — e.g. `className="contents"` makes the header and
 * the sticky slot direct children of the screen's own section.
 */
export function Masthead({
  overline,
  title,
  meta,
  actions,
  collapsed,
  children,
  className,
}: MastheadProps) {
  const pinnedRef = useRef<HTMLDivElement>(null);
  const pinned = children != null && children !== false;

  // Pinned children sit under the compact title: grow the root's
  // scroll-padding-top by their height so focus never lands beneath them
  // (WCAG 2.4.11). One read on mount, not per scroll.
  useEffect(() => {
    const el = pinnedRef.current;
    if (!el) return;
    const root = document.documentElement.style;
    root.setProperty('--sticky-pinned-h', `${el.offsetHeight}px`);
    return () => {
      root.removeProperty('--sticky-pinned-h');
    };
  }, [pinned]);

  return (
    <div className={cn(className)}>
      <ScreenHeader
        kicker={overline}
        title={title}
        meta={meta}
        actions={actions}
        className={pinned ? 'pb-4 sm:pb-6' : 'pb-8 sm:pb-12'}
      />

      <div
        ref={pinned ? pinnedRef : undefined}
        className={cn(
          'pointer-events-none sticky top-[var(--masthead-compact-h)] z-20 -mt-8',
          pinned ? 'pb-5 sm:pb-9' : 'h-0',
        )}
      >
        <div
          aria-hidden
          className={cn(
            'absolute inset-x-0 bottom-full h-[var(--masthead-compact-h)] pt-1',
            collapsed ? 'pointer-events-auto bg-ambient-canvas' : 'bg-transparent',
          )}
        >
          <AnimatePresence>
            {collapsed && (
              <motion.p
                aria-hidden
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { duration: DUR.fast } }}
                exit={{ opacity: 0, transition: { duration: DUR.fast } }}
                className="truncate py-1.5 font-display text-headline text-ink"
              >
                {title}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
        {/* Pinned children always get the solid canvas fill: they can overlap
            scrolled content before `collapsed` flips. */}
        {pinned ? <div className="pointer-events-auto bg-ambient-canvas pb-3">{children}</div> : null}
      </div>
    </div>
  );
}
